import { ReactElement, useEffect, useState } from 'react'
import { AsyncThunkAction, AsyncThunkConfig } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'
import { sort, uniq } from 'ramda'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchCompletedEvents,
  fetchRecertificationCycleCategories,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
} from 'src/v3/profile/profile.slice'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from '@mui/material'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { RecertificationCycleCategory } from 'src/v3/core/types'

const DEFAULT_CATEGORIES = [
  'Airway/Respiratory/Ventilation',
  'Cardiovascular',
  'Critical Care',
  'Medical',
  'Operations',
  'Pediatrics',
  'Trauma',
]

type CategoryFormProps = {
  cycleID: string
  category?: Partial<RecertificationCycleCategory>
  refetchCycles: AsyncThunkAction<any, any, AsyncThunkConfig>
  open: boolean
  setOpen: (status: boolean) => void
}

export let CategoryForm = ({
  cycleID,
  category,
  refetchCycles,
  open,
  setOpen,
}: CategoryFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)

  let [saving, setSaving] = useState(false)

  let categories = allCategories[cycleID || ''] || []
  let groups = sort(
    (a, b) => a.localeCompare(b),
    uniq(categories.map((c) => c.subcategory || '').filter((c) => !!c)),
  )

  let form = useForm({
    defaultValues: {
      id: category?.id,
      name: category?.name || '',
      hours: category?.hours?.toString() || '',
      subcategory: category?.subcategory || '',
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let request = !value.id ? axios.post : axios.put
      request<RecertificationCycleCategory>(
        `/api/profile/recertification-cycle/category`,
        {
          id: value.id,
          cycleID: !value.id ? cycleID : undefined,
          name: value.name,
          hours: value.hours,
          subcategory: value.subcategory || undefined,
          categoryType: !value.id
            ? category?.categoryType || 'standard'
            : undefined,
        },
      )
        .then(() => dispatch(fetchRecertificationCycleCategories(cycleID)))
        .then(() => dispatch(refetchCycles))
        .then(() => dispatch(fetchCompletedEvents(cycleID)))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(
            pluckError(err, 'Error Creating Recertification Cycle Category'),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  useEffect(() => {
    if (category) {
      form.setFieldValue('id', category.id)
      form.setFieldValue('name', category.name || '')
      form.setFieldValue('hours', category.hours?.toString() || '')
      form.setFieldValue('subcategory', category.subcategory || '')
    }
  }, [category?.id, category?.categoryType])

  useEffect(() => {
    if (
      cycleID &&
      (!categoriesStatus[cycleID] ||
        categoriesStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleCategories(cycleID))
    }
  }, [cycleID, categoriesStatus])

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 'fit-content', px: 2, py: 1, minWidth: 320 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Create New Category
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {category?.categoryType !== 'live' && (
              <form.Field
                name="subcategory"
                children={(field) => (
                  <Autocomplete
                    freeSolo
                    value={field.state.value}
                    onChange={(_, val) => field.handleChange(val || '')}
                    onInputChange={(_, val) => field.handleChange(val)}
                    options={groups}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Group"
                        sx={{ bgcolor: 'background.paper' }}
                        name={field.name}
                        error={field.state.meta.errors.length > 0}
                        helperText={field.state.meta.errors.join(', ')}
                      />
                    )}
                  />
                )}
              />
            )}
            <form.Field
              name="name"
              validators={{
                onSubmit: ({ value }) =>
                  !value ? 'Name is a required field' : undefined,
              }}
              children={(field) => (
                <Autocomplete
                  sx={{ my: 0.5 }}
                  freeSolo
                  value={field.state.value}
                  onChange={(_, val) => field.handleChange(val || '')}
                  onInputChange={(_, val) => field.handleChange(val)}
                  options={DEFAULT_CATEGORIES}
                  disabled={category?.categoryType === 'live'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Name"
                      sx={{ bgcolor: 'background.paper' }}
                      name={field.name}
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                />
              )}
            />
            <form.Field
              name="hours"
              validators={{
                onSubmit: ({ value }) =>
                  // Number() rather than parseFloat() so "1.2.3" and "." fail too.
                  !(Number(value) > 0)
                    ? 'Hours must be greater than 0'
                    : undefined,
              }}
              children={(field) => (
                <TextField
                  sx={{ bgcolor: 'background.paper', mb: 0.5 }}
                  required
                  fullWidth
                  variant="outlined"
                  value={field.state.value}
                  label="Hours"
                  name={field.name}
                  onChange={(e) =>
                    field.handleChange(e.target.value.replace(/[^\d.]/g, ''))
                  }
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
